import type { MatchState, PlayerState } from './state';
import type { PlayerInput } from '../types';
import type { SimConfig } from '../config/simConfig';
import type { SimEvent } from './events';
import { DT } from '../config/simConfig';
import { cloneState } from './clone';
import { applyActions, applyRagdolls } from './actions';
import { applyKicks, type KickRequest } from './ballActions';
import { integratePlayers } from './movement';
import { NULL_INPUT } from './input';
import { stepBall } from '../physics/ball';
import { resolveBallPosts } from '../physics/world';
import { PITCH } from '../config/pitchConfig';
import { resolvePlayerCollisions, resolveBallPlayerCollisions, type ContactEvent } from '../physics/collide';
import { annotateTackleContacts, resolveTackleDispossession, resolveTrap } from './tackles';
import { resolvePossession, resolveDeadBallCarry, resolveDeadBallClaim } from './possession';
import { resolveGkAutoDive, resolveGkSave, resolveGkSmother, resolveGkBoxExit, clearGkLock } from './gk';
import { resolveBarge, resolveViolence } from './violence';
import { containBounds, reflectCageWalls } from './bounds';
import { advancePhase } from '../rules/phase';
import { applyRules, resolveSuspensions } from '../rules/apply';
import { refereeStep } from '../referee/step';

export interface StepResult {
  state: MatchState;
  events: SimEvent[];
}

/**
 * Advance the match one tick (§5.1). PURE: runs on a clone, never mutates its arguments (T-ARCH-003).
 * `inputs.length === players.length`. M1 implements the movement/ball/collision/kick/ragdoll core; the
 * phase machine (M3), possession (§5.6), referee (M5), and rules (M3) slot into the marked steps.
 */
export function step(state: MatchState, inputs: readonly PlayerInput[], cfg: SimConfig): StepResult {
  const s = cloneState(state);
  const events: SimEvent[] = [];
  const kicks: KickRequest[] = [];

  // §5.1 canonical sub-step order (normative):
  advancePhase(s, events, cfg); // 1 — count timed phases down; expiry → next kickoff (§6.1)
  applyActions(s, inputs, cfg, kicks, events); // 2 (+ D18 keeper pickup)
  // 3 — D108: during GOAL_CELEBRATION the ceremony owns movement (applyRules, step 8, writes the
  // orbit/trudge velocities), so player input must not steer here or it would fight it every tick.
  // Only THIS step is gated: applyActions and finalize still see the real buttons, so no held button
  // turns into a spurious rising edge when the ceremony ends and the kickoff begins.
  integratePlayers(s, s.phase === 'GOAL_CELEBRATION' ? s.players.map(() => NULL_INPUT) : inputs, cfg);
  const ballPrev = { x: s.ball.pos.x, y: s.ball.pos.y, z: s.ball.pos.z }; // D52: this tick's travel start (swept ball↔player)
  if (s.ball.possessor === null) {
    stepBall(s.ball, cfg.conditions[s.conditions], cfg.ball, DT); // 4 (free ball only)
    resolveBallPosts(s.ball, PITCH[s.mode.teamSize], cfg); // 4a — bounce off the goalposts/crossbar (§6.2)
  }
  applyKicks(s, kicks, cfg, events); // 4b — launch queued kicks with exact velocity (drag begins next tick)
  const contacts = resolvePlayerCollisions(s.players, cfg.collision, cfg.actions.block.BRACE_MUL); // 5 (player↔player / ref; D82 braced blockers hold ground)
  resolveBallPlayerCollisions(s, cfg, s.tick, ballPrev); // 5 (ball↔player, swept — D52)
  recordLastContacts(s, contacts); // 5b0 — D101: stamp who hit whom, for the §7.4 dive judgement
  annotateTackleContacts(s, contacts, cfg); // 5c — mark ballFirst BEFORE ragdoll (§7.1)
  resolveTackleDispossession(s, cfg, events); // 5d — ball-first tackle takes possession (§5.4) before ragdoll
  resolveTrap(s, cfg, events); // 5d1 — D45 catch/trap: gather a loose ball / clean-side poke (never a foul)
  applyRagdolls(s, contacts, cfg, events); // 5b — high-impulse contacts ⇒ ragdoll; ball-first tackler exempt (§5.4d)
  const punchContacts = resolveViolence(s, cfg, events); // 5d2 — punches land: ragdoll + impulse + PUNCH_LANDED
  resolveBarge(s, cfg, events); // 5d3 — D40 legal shoulder barge staggers the carrier, ball runs loose
  resolveGkAutoDive(s, cfg); // 5e0 — D133: nobody dove and the standing hands will not reach it
  resolveGkSave(s, cfg, events); // 5e
  resolveGkSmother(s, cfg, events); // 5f — D18 box smother
  resolveGkBoxExit(s); // 5g — D18 carrying the held ball out of the box puts it down — diving keeper catches (→GK_HOLD) or parries the ball (§5.4/§5.6)
  // 6 — one slot, two variants: the live-ball possession pass, or the dead-ball pair while a restart is
  // pending — D129's carry (6a: keep/lose + ride his feet) then D122's narrow claim (6b). §5.1 order is
  // untouched; the carry runs FIRST so a taker who has just lost it can be re-gathered the same tick.
  if (s.phase === 'PLAY') resolvePossession(s, cfg, events, inputs); // (+ D40 body-block reads CONTAIN)
  else if (s.phase === 'KICKOFF' || s.phase === 'RESTART_PENDING') {
    resolveDeadBallCarry(s, cfg, events);
    resolveDeadBallClaim(s, cfg, events);
  }
  clearGkLock(s); // D18 — re-handle lock lifts once anyone else touches — soft-magnetism (§5.6); the ball is dead in restart/dead-ball phases
  refereeStep(s, punchContacts.length ? [...contacts, ...punchContacts] : contacts, cfg, events); // 7 — fouls/cards + ref movement (§7)
  resolveSuspensions(s, events); // 7c — D65c: a red-card suspension elapsed ⇒ the player RETURNS
  reflectCageWalls(s, cfg); // 7b — D40 cage boards on small pitches (before rules: outs can't happen)
  applyRules(s, inputs, events, cfg); // 8 — goals/outs/restarts/offside/phase (§6)
  containBounds(s, cfg); // config-gated arena walls (default OFF; superseded by out-of-play for rules matches)
  finalize(s, inputs, cfg); // 9

  return { state: s, events };
}

/** Per-player finalize (§5.1 step 9): stamina, action timer, edge-trigger memory. Shared with stepEntity. */
export function finalizePlayer(p: PlayerState, input: PlayerInput | undefined, cfg: SimConfig): void {
  const mv = cfg.movement;
  const speed = Math.hypot(p.vel.x, p.vel.y);
  if (p.sprinting) p.stamina -= mv.SPRINT_DRAIN * DT;
  else if (speed < mv.WALK_SPEED) p.stamina += mv.STAMINA_REGEN * DT;
  if (p.stamina < 0) p.stamina = 0;
  else if (p.stamina > 1) p.stamina = 1;
  p.actionTicks += 1;
  // D48: full u32 — buttons outgrew u16 in D45 (BTN.TRAP = 1<<16); the old 0xffff mask stripped bit 16
  // from the edge memory, so a HELD trap re-fired on every expiry (the rooted "stuck in duels" loop).
  p.prevButtons = (input?.buttons ?? 0) >>> 0;
}

/** Step 9: per-player finalize + clock, cooldowns, tick advance (§5.1). */
function finalize(s: MatchState, inputs: readonly PlayerInput[], cfg: SimConfig): void {
  for (const p of s.players) finalizePlayer(p, inputs[p.id], cfg);
  if (s.ball.claimCooldownTicks > 0) s.ball.claimCooldownTicks -= 1;
  if (s.phase === 'PLAY') s.clockTicks += 1;
  s.tick += 1;
}

/**
 * D101 — record each player's most recent opponent/teammate contact (§7.4's input).
 *
 * `PlayerState.lastContact` was declared, cloned, encoded, decoded and hashed, and `judgeDive` keys its
 * `timing01`, its impulse-based `momentum01` AND its whole `oppContact` branch on it — but NOTHING in the
 * simulation ever wrote it. So in a live match `oppContact` was always false ⇒ `contactAge = Infinity` ⇒
 * `timing01 = 0`, and the first verdict branch (`(!oppContact || age > DIVE_WINDOW) && info >= I_MIN`) fired
 * for EVERY dive the referee could see: verdict CAUGHT, a yellow for simulation, and the free kick to the
 * opponents. §7.4's "win a foul by diving" was dead code and `divesWon` could never increment. The dive
 * tests passed only because they hand-build a `lastContact` the sim never produced.
 *
 * The impulse is the contact impulse along the collision normal, signed per participant (the direction each
 * body was actually pushed), which is exactly what `judgeDive`'s momentum term expects. Runs at 5b0 — after
 * the collision pass that produced the contacts, and before ragdoll/referee read them.
 */
function recordLastContacts(s: MatchState, contacts: ContactEvent[]): void {
  for (const c of contacts) {
    const pa = s.players[c.a];
    const pb = s.players[c.b];
    if (!pa || !pb) continue;
    // n points a→b, so b is pushed along +n and a along −n
    pa.lastContact = { by: pb.id, tick: s.tick, impulse: { x: -c.n.x * c.impulse, y: -c.n.y * c.impulse } };
    pb.lastContact = { by: pa.id, tick: s.tick, impulse: { x: c.n.x * c.impulse, y: c.n.y * c.impulse } };
  }
}
